import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Typography, Stack, Collapse, Skeleton, Link } from '@mui/material';
import type { Theme } from '@mui/material';
import { Box, SxProps } from '@mui/system';
import axios from 'src/lib/axios';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import { BusinessHoursDTO } from 'src/api/dto/organization/businessHours';
import { OrganizationDTO } from 'src/api/dto/organization/organization';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EnSocialMediaType } from 'src/api/dto/socialmedia/enSocialMediaType';
import { SocialMediaDTO } from 'src/api/dto/socialmedia/socialMedia';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import DateHelper from '../../../utils/dateHelper';
import OpeningHours from './OpeningHours';
import ExpandMore from '../expands/ExpandMore';

const toDate = (dateStr: string) => new Date(`${DateHelper.toISOLocalDateTime(new Date()).split('T')[0]} ${dateStr}`);
const formatTime = (dateStr: string) => toDate(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
const isTimeBetween = (now: Date, dateFrom: Date, dateTo: Date) => {
  if (now.getTime() >= dateFrom.getTime() && now.getTime() <= dateTo.getTime()) return true;

  return false;
};
const findOpenSlot = (slots: BusinessHoursDTO[]) => {
  if (slots) {
    return slots.find((x) => x.day === (new Date().getDay() === 0 ? 7 : new Date().getDay()) && isTimeBetween(new Date(), toDate(x.openTime), toDate(x.closeTime)) && !x.closed);
  }

  return null;
};

const findNextOpenSlot = (slots: BusinessHoursDTO[]) => {
  const availableSlots = slots.filter((x) => x.openTime !== x.closeTime);
  if (availableSlots) {
    let dayIdx = new Date().getDay();
    let slot: BusinessHoursDTO;

    if (dayIdx === 0) {
      dayIdx = 7;
    }

    let i = dayIdx;
    do {
      // eslint-disable-next-line @typescript-eslint/no-loop-func
      const possibleSlots = availableSlots.filter((x) => x.day === i && !x.closed);
      if (i === dayIdx) {
        const possibleSlot = possibleSlots.find((x) => toDate(x.openTime).getTime() >= new Date().getTime());
        if (possibleSlot) {
          slot = possibleSlot;
        } else if (availableSlots.findIndex((x) => x.day !== dayIdx && !x.closed) === -1) {
          slot = possibleSlots.shift();
        }
      } else if (possibleSlots.length > 0) {
        slot = possibleSlots.shift();
      }

      i++;
      if (i > 7) {
        i = 1;
      }
    } while (!slot && i !== dayIdx);

    return slot;
  }

  return null;
};

interface OpeningNowProps {
  organizationId: number;
  colorCode?: string;
  organization?: OrganizationDTO;
  showAddress?: boolean;
  showAllSlots?: boolean;
  sx?: SxProps<Theme>;
}

const OpenNow: FC<OpeningNowProps> = (props) => {
  const { organizationId, colorCode, organization, showAddress, sx, showAllSlots } = props;
  const isMountedRef = useIsMountedRef();
  const [businessHours, setBusinessHours] = useState<BusinessHoursDTO[] | null>(
    null
  );
  const [openSlot, setOpenSlot] = useState<BusinessHoursDTO | null>(null);
  const [nextOpenSlot, setNextOpenSlot] = useState<BusinessHoursDTO | null>(null);
  const [phone, setPhone] = useState<SocialMediaDTO | null>(null);
  const [email, setEmail] = useState<SocialMediaDTO | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const getBusinessHours = useCallback(async (id: number) => {
    try {
      const response = await axios.get<BusinessHoursDTO[]>(
        `/api/businesshours/${id}`
      );
      if (isMountedRef.current) {
        if (response.data) {
          setBusinessHours(response.data);
          setOpenSlot(findOpenSlot(response.data));
          setNextOpenSlot(findNextOpenSlot(response.data));
        }
        setIsLoaded(true);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMountedRef]);

  const getContactInformation = useCallback(async () => {
    try {
      const response = await axios.get<SocialMediaDTO[]>(
        '/api/socialmedia', { params: { organizationnr: organizationId } }
      );
      if (isMountedRef.current) {
        const phoneInfo = response.data.find((x) => x.enumSocialMediaType === EnSocialMediaType.Phone);
        const emailInfo = response.data.find((x) => x.enumSocialMediaType === EnSocialMediaType.Email);

        if (phoneInfo) {
          setPhone(phoneInfo);
        }

        if (emailInfo) {
          setEmail(emailInfo);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMountedRef]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    getBusinessHours(organizationId);
    if (showAddress) {
      getContactInformation();
    }
  }, [getBusinessHours]);

  if (!isLoaded) {
    return (
      <Skeleton
        height={30}
        width="100%"
      />
    );
  }

  const getOpenDay = (nextOpenDay:BusinessHoursDTO) => {
    const now = new Date();

    if (nextOpenDay.day === now.getDay() && toDate(nextOpenDay.openTime).getTime() > now.getTime()) {
      return 'heute';
    }
    return `am ${DateHelper.getWeekDayNameShort(nextOpenDay.day)}`;
  };

  return (
    <Box sx={sx}>
      {showAddress && organization && (
        <Box sx={{ paddingBottom: 0 }}>
          {phone && (
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            variant="body2"
            gutterBottom
            component="div"
          >
            <LocalPhoneIcon
              fontSize="small"
              sx={{ color: colorCode }}
            />
            <Link
              href={phone.website}
              color="textPrimary"
              variant="subtitle2"
              target="_blank"
              sx={{ pl: 0.5 }}
            >
              {phone.profile}
            </Link>
          </Typography>
          )}
          {email && (
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            variant="body2"
            gutterBottom
            component="div"
          >
            <EmailIcon
              fontSize="small"
              sx={{ color: colorCode }}
            />
            <Link
              href={email.website}
              color="textPrimary"
              variant="subtitle2"
              target="_blank"
              sx={{ pl: 0.5 }}
            >
              {email.profile}
            </Link>
          </Typography>
          )}
          <Typography
            sx={{ fontWeight: 'bold', pt: 1 }}
            variant="body2"
            gutterBottom
            component="div"
          >
            {organization.streetName}
            {' '}
            {organization.streetNumber}
            ,
            {' '}
            {organization.city}
            ,
            {' '}
            {' '}
            {organization.zipCode}
          </Typography>
        </Box>
      )}
      {(openSlot || nextOpenSlot) && (
        <Stack
          direction="row"
          alignItems="center"
          display="flex"
        >
          {openSlot && (
          <Typography
            color={colorCode}
            sx={{ fontWeight: 'bold' }}
            variant="body2"
            gutterBottom
            component="div"
          >
            Geöffnet bis
            {' '}
            {formatTime(openSlot.closeTime)}
            {' '}
            Uhr
          </Typography>
          )}
          {!openSlot && (
          <>
            {nextOpenSlot && (
              <Typography
                sx={{ fontWeight: 'bold' }}
                variant="body2"
                gutterBottom
                component="div"
              >
                Öffnet
                {' '}
                {getOpenDay(nextOpenSlot)}
                {' '}
                um
                {' '}
                {formatTime(nextOpenSlot.openTime)}
                {' '}
                Uhr
              </Typography>
            )}
            {!nextOpenSlot && (
              <Typography
                sx={{ fontWeight: 'bold' }}
                variant="body2"
                gutterBottom
                component="div"
              >
                Derzeit geschlossen
              </Typography>
            )}
          </>
          )}
          {showAllSlots && (
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
          )}
        </Stack>
      )}
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
      >
        <OpeningHours businessHours={businessHours} />
      </Collapse>
    </Box>
  );
};

OpenNow.defaultProps = {
  showAddress: true,
  showAllSlots: true,
  colorCode: '#000000',
};

export default OpenNow;
